/**
 * STT Bridge — Speech-to-text with two backends:
 *   1. Web Speech Recognition API (native, works on all phones, free)
 *   2. Whisper WASM Web Worker fallback (for browsers without Web Speech API)
 *
 * The Web Speech API is preferred because it works instantly on iOS/Android
 * without downloading a 39MB model. Whisper is only used as a fallback.
 */
import type { STTWorkerMessage } from '../types/index';
import { getLangMeta } from '../i18n';

// Check for Web Speech API support
const SpeechRecognitionClass = (window as any).SpeechRecognition
  || (window as any).webkitSpeechRecognition;

export class STTBridge {
  private worker: Worker | null = null;
  private workerReady = false;
  private useNativeSTT = false;
  private recognition: any = null;
  private recording = false;

  // Whisper fallback resources
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private audioChunks: Float32Array[] = [];

  // Native STT result
  private nativeResolve: ((text: string) => void) | null = null;
  private nativeReject: ((err: Error) => void) | null = null;

  // ------------------------------------------------------------------ lifecycle

  async init(): Promise<void> {
    // Prefer native Web Speech API — instant, no download, works on phones
    if (SpeechRecognitionClass) {
      this.useNativeSTT = true;
      console.log('[STT] Using native Web Speech Recognition API');
      return;
    }

    // Fallback: Whisper WASM in Web Worker
    console.log('[STT] Native STT unavailable, loading Whisper WASM...');
    return new Promise<void>((resolve, reject) => {
      this.worker = new Worker(
        new URL('./stt-worker.ts', import.meta.url),
        { type: 'module' },
      );

      const onMessage = (e: MessageEvent<STTWorkerMessage>) => {
        const msg = e.data;
        if (msg.type === 'ready') {
          this.workerReady = true;
          this.worker!.removeEventListener('message', onMessage);
          resolve();
        } else if (msg.type === 'error') {
          this.worker!.removeEventListener('message', onMessage);
          reject(new Error(msg.error ?? 'Unknown worker init error'));
        }
      };

      this.worker.addEventListener('message', onMessage);
      this.worker.postMessage({ type: 'init' });
    });
  }

  destroy(): void {
    this.stopRecordingResources();
    if (this.recognition) {
      try { this.recognition.abort(); } catch { /* ignore */ }
      this.recognition = null;
    }
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerReady = false;
  }

  isReady(): boolean {
    return this.useNativeSTT || this.workerReady;
  }

  // ----------------------------------------------------- listening

  async startListening(): Promise<void> {
    if (this.recording) return;
    this.recording = true;

    if (this.useNativeSTT) {
      // Native Web Speech API — just start recognition
      return;
    }

    // Whisper path: capture mic audio
    this.audioChunks = [];
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
    });
    this.audioContext = new AudioContext({ sampleRate: 16000 });
    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
    const bufferSize = 4096;
    this.processorNode = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
    this.processorNode.onaudioprocess = (event: AudioProcessingEvent) => {
      if (!this.recording) return;
      const input = event.inputBuffer.getChannelData(0);
      this.audioChunks.push(new Float32Array(input));
    };
    this.sourceNode.connect(this.processorNode);
    this.processorNode.connect(this.audioContext.destination);
  }

  async stopListening(): Promise<string> {
    if (!this.recording) {
      throw new Error('Not currently recording.');
    }
    this.recording = false;

    if (this.useNativeSTT) {
      return this.nativeRecognize();
    }

    // Whisper path
    this.stopRecordingResources();
    const merged = this.mergeChunks(this.audioChunks);
    this.audioChunks = [];
    if (merged.length === 0) return '';
    return this.transcribe(merged);
  }

  // -------------------------------------------------------- native Web Speech API

  private nativeRecognize(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      // Request mic permission first, then use recognition
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        // Got permission, stop the stream (recognition handles its own audio)
        stream.getTracks().forEach(t => t.stop());

        const recognition = new SpeechRecognitionClass();
        recognition.lang = getLangMeta().speechLang;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.continuous = false;

        recognition.onresult = (event: any) => {
          const transcript = event.results[0]?.[0]?.transcript || '';
          resolve(transcript);
        };

        recognition.onerror = (event: any) => {
          if (event.error === 'no-speech') {
            resolve('');
          } else {
            reject(new Error(`Speech recognition error: ${event.error}`));
          }
        };

        recognition.onend = () => {
          // If no result was fired, resolve empty
          resolve('');
        };

        // Prevent double-resolve
        let resolved = false;
        const origResolve = resolve;
        const origReject = reject;
        resolve = ((val: string) => { if (!resolved) { resolved = true; origResolve(val); } }) as typeof resolve;
        reject = ((err: Error) => { if (!resolved) { resolved = true; origReject(err); } }) as typeof reject;

        recognition.start();
        this.recognition = recognition;
      }).catch((err) => {
        reject(new Error('Microphone access denied: ' + err.message));
      });
    });
  }

  // -------------------------------------------------------- Whisper transcribe

  async transcribe(audioData: Float32Array): Promise<string> {
    if (!this.worker || !this.workerReady) {
      throw new Error('STT worker is not initialized.');
    }
    return new Promise<string>((resolve, reject) => {
      const onMessage = (e: MessageEvent<STTWorkerMessage>) => {
        const msg = e.data;
        if (msg.type === 'result') {
          this.worker!.removeEventListener('message', onMessage);
          resolve(msg.text ?? '');
        } else if (msg.type === 'error') {
          this.worker!.removeEventListener('message', onMessage);
          reject(new Error(msg.error ?? 'Transcription failed'));
        }
      };
      this.worker!.addEventListener('message', onMessage);
      this.worker!.postMessage({ type: 'transcribe', audio: audioData });
    });
  }

  // ------------------------------------------------------------------- internal

  private stopRecordingResources(): void {
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode.onaudioprocess = null;
      this.processorNode = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.audioContext) {
      void this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) { track.stop(); }
      this.mediaStream = null;
    }
  }

  private mergeChunks(chunks: Float32Array[]): Float32Array {
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    return merged;
  }
}
